import { Document, Page, Text, View, StyleSheet, Image, Link } from '@react-pdf/renderer';
import { IGetRegistration } from '../../services/hooks/Registration/useGetRegistration';
import { IAspectsAndTopicsProps, ITitleTopics } from '../../services/hooks/Titles/useGetTitlesTopicsData';
import React from 'react';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontSize: 10,
    fontFamily: 'Roboto',
  },
  header: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2 solid #000000',
  },
  logos: {
    flexDirection: 'row',
    width: '100%',
    gap: 10,
    justifyContent: 'center',
  },
  logoPlaceholder: {
    width: 70,
    height: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  processInfo: {
    flex: 1,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  processName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 13,
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  candidateInfo: {
    fontSize: 10,
    display: 'flex',
    flexDirection: 'column',
    marginVertical: 8,
    marginBottom: 16,
    lineHeight: 1.3,
  },
  documentTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginVertical: 15,
    fontSize: 10,
  },
  dateOfIssue: {
    textAlign: 'right',
    fontWeight: 'bold',
    marginVertical: 8,
    fontSize: 9,
  },
  sectionTitle: {
    backgroundColor: '#D1D1D1',
    color: '#707070',
    padding: 8,
    fontWeight: 'bold',
    marginBottom: 7,
    borderRadius: 4,
    fontSize: 10,
  },
  table: {
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#D1D1D1',
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 20,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  tableColHeader: {
    width: '33.3%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#D1D1D1',
    backgroundColor: '#f0f0f0',
    padding: 5,
    fontWeight: 'bold',
    fontSize: 9,
  },
  tableCol: {
    width: '33.3%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#D1D1D1',
    padding: 5,
    fontSize: 9,
  },
  footer: {
    marginTop: 30,
    textAlign: 'center',
    fontSize: 9,
    color: '#666',
    borderTop: '1 solid #ddd',
    paddingTop: 10,
  },
  protocolInfo: {
    marginTop: 20,
    fontSize: 9,
    textAlign: 'right',
  },
  boldText: {
    fontWeight: 'bold',
  },
  flex: { display: 'flex', flexDirection: 'row' },
});

const Table: React.FC<{ data: ITitleTopics[] }> = ({ data }) => (
  <View style={styles.table}>
    <View style={styles.tableRow}>
      <Text style={styles.tableColHeader}>Tópico</Text>
      <Text style={styles.tableColHeader}>Data de Envio</Text>
      <Text style={styles.tableColHeader}>Anexo</Text>
    </View>

    {/* Linhas da tabela */}
    {data?.map((row, index) => {
      if (row.titles_send.length < 1) return;

      return (
        <View style={styles.tableRow} key={index}>
          <Text style={styles.tableCol}>{row.name}</Text>
          <View style={{ display: 'flex', width: '100%' }}>
            {row.titles_send.map((title) => (
              <View key={title.id} style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <Text style={styles.tableCol}>{title?.created_at?.br}</Text>
                <View style={styles.tableCol}>
                  {title?.attachements?.map((acc, idx) => (
                    <Link key={acc.id} href={acc.url} style={{ textDecoration: 'none' }}>
                      Anexo {idx + 1}
                    </Link>
                  ))}
                </View>
              </View>
            ))}
          </View>
        </View>
      );
    })}
  </View>
);

interface IOwnProps {
  data?: IAspectsAndTopicsProps[];
  portrait_logo?: string;
  instituteLogo?: string;
  registrationData?: IGetRegistration | null;
}

const currentDateHour = new Date();

const ProofOfSendingOfTitle: React.FC<IOwnProps> = ({ instituteLogo, portrait_logo, registrationData, data }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.logos}>
          <View style={styles.logoPlaceholder}>
            <Image src={instituteLogo} />
          </View>
          <View style={styles.processInfo}>
            <Text style={styles.processName}>{registrationData?.selective_process_vacancy.selective_process.name}</Text>
            <View style={styles.flex}>
              <Text style={styles.boldText}>
                {' '}
                {registrationData?.selective_process_vacancy?.cultural_house
                  ? registrationData?.second_vacancy
                    ? 'Turma - Primeira opção'
                    : 'Turma'
                  : 'Vaga / Cargo'}
                :{' '}
              </Text>
              <Text>{registrationData?.selective_process_vacancy?.name}</Text>
            </View>
            {registrationData?.second_vacancy && (
              <View style={{ ...styles.flex, marginTop: 2 }}>
                <Text style={styles.boldText}> Turma - Segunda opção: </Text>
                <Text>{registrationData?.selective_process_vacancy?.name}</Text>
              </View>
            )}
          </View>
          <View style={styles.logoPlaceholder}>
            <Image src={portrait_logo} />
          </View>
        </View>
        <Text style={styles.documentTitle}>COMPROVANTE DE ENVIO DE TÍTULOS</Text>
      </View>
      <Text style={styles.dateOfIssue}>
        Documento emitido em: {currentDateHour.toLocaleString('pt-BR')?.replace(',', '')}
      </Text>
      <View style={styles.candidateInfo}>
        <View style={styles.flex}>
          <Text style={styles.boldText}>Inscrição: </Text>
          <Text>{registrationData?.registration_code}</Text>
        </View>
        <View style={styles.flex}>
          <Text style={styles.boldText}>Candidato: </Text> <Text>{registrationData?.candidate?.user?.full_name}</Text>
        </View>
      </View>
      {data?.map((title) => (
        <React.Fragment key={title.id}>
          <Text style={styles.sectionTitle}>{title?.name}</Text>
          <Table data={title?.titles_topics} />
        </React.Fragment>
      ))}
    </Page>
  </Document>
);

export default ProofOfSendingOfTitle;
